import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronRight, ClipboardCheck, FileBarChart2, FolderKanban, LifeBuoy, Minus, SendHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { usePermissions } from "@/lib/permissions";
import { Markdown, Thinking } from "@/pages/AskBuildNexa";
import botIcon from "@/assets/buildnexa-bot.png";

const KEY = "buildnexa.assistant.chat";
const HELLO_KEY = "buildnexa.assistant.hello";
const remember = (id) => { try { if (id) sessionStorage.setItem(KEY, id); else sessionStorage.removeItem(KEY); } catch { /* private window */ } };
const recall = () => { try { return sessionStorage.getItem(KEY); } catch { return null; } };

const MAROON = "#910029";
const HEADER_BG = "linear-gradient(135deg, #a3002f 0%, #910029 45%, #6e001f 100%)";
const EASE = [0.22, 1, 0.36, 1];
const LAUNCHER = 48;      // px, the round robot button
const EDGE = 24;          // px from the right and bottom edges (desktop)

const QUICK = [
  { title: "Track project status", hint: "Progress, delays and milestones", icon: FolderKanban, ask: "What is the status of my ongoing projects: progress, delays and upcoming milestones?" },
  { title: "Check pending approvals", hint: "Purchase documents waiting on someone", icon: ClipboardCheck, ask: "Which indents, purchase orders and work orders are waiting for approval?" },
  { title: "Find a report", hint: "See what I can report on", icon: FileBarChart2, ask: "Which reports can you give me? List them briefly." },
  { title: "Search ERP help", hint: "Step-by-step help for any job", icon: LifeBuoy, ask: null, prefill: "How do I " },
];

/** Where the panel may sit: below the top navigation bar, right of the sidebar, inside the window. Measured, never assumed. */
function measure() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const narrow = vw < 640;
  const topbar = document.querySelector('[data-testid="app-topbar"]')?.getBoundingClientRect();
  const sidebar = document.querySelector('[data-testid="app-sidebar"]')?.getBoundingClientRect();
  const edge = narrow ? 8 : EDGE;
  const top = Math.max(topbar && topbar.height ? topbar.bottom : 0, 0) + (narrow ? 8 : 12);
  const left = sidebar && sidebar.width ? sidebar.right + 16 : edge;
  const width = narrow ? vw - 2 * edge : Math.max(Math.min(540, vw - left - edge), Math.min(420, vw - 2 * edge));   // wide, but never over the sidebar
  return { top, right: edge, bottom: edge, width, height: Math.max(vh - top - edge, 320), vh };
}

/** BuildNexa AI on every page. The robot waits in the bottom-right corner; a click sends it up the right edge on a thin glowing
 *  trail while the support panel unfolds upward beneath it, stopping just below the top bar. Minimise or close plays it backwards.
 *  The animation lives only here (phase); the conversation lives in <Panel> and survives minimising.
 *  Shown to people whose access includes the AI assistant (Admins always); hidden on the full Ask BuildNexa page and in print. */
export default function AssistantBubble() {
  const { user } = useAuth();
  const { canView, loading } = usePermissions();
  const { pathname } = useLocation();
  const reduce = useReducedMotion();
  const [phase, setPhase] = useState("closed");         // closed → opening → open → closing → closed
  const [box, setBox] = useState(null);
  const [mounted, setMounted] = useState(false);         // the panel is kept after the first open, so minimising keeps the chat
  const [unread, setUnread] = useState(0);
  const [session, setSession] = useState(0);             // bumps on close: the next open starts a fresh conversation
  const [hello, setHello] = useState(false);             // a short greeting beside the robot after each login (AuthContext clears the flag)
  const launcherRef = useRef(null);
  const resetAfter = useRef(false);
  const returnFocus = useRef(false);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const dismissHello = () => { setHello(false); try { sessionStorage.setItem(HELLO_KEY, "1"); } catch { /* private window */ } };
  useEffect(() => {
    let seen = false;
    try { seen = sessionStorage.getItem(HELLO_KEY) === "1"; } catch { /* private window */ }
    if (seen) return undefined;
    const show = setTimeout(() => setHello(true), 1200);
    const hide = setTimeout(() => { setHello(false); try { sessionStorage.setItem(HELLO_KEY, "1"); } catch { /* private window */ } }, 15000);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, []);

  const openPanel = useCallback(() => {
    if (phaseRef.current !== "closed") return;           // a second click mid-animation is ignored
    dismissHello();
    setBox(measure());
    setMounted(true);
    setUnread(0);
    setPhase("opening");
  }, []);
  const closePanel = useCallback((reset) => {
    if (phaseRef.current !== "open") return;
    resetAfter.current = !!reset;
    setBox(measure());
    setPhase("closing");
  }, []);
  const finished = useCallback(() => {
    if (phaseRef.current === "opening") setPhase("open");
    else if (phaseRef.current === "closing") {
      setPhase("closed");
      if (resetAfter.current) { remember(null); setSession((n) => n + 1); resetAfter.current = false; }
      returnFocus.current = true;
    }
  }, []);

  useEffect(() => {                                       // after closing, keyboard focus goes back to the robot
    if (phase === "closed" && returnFocus.current) { returnFocus.current = false; launcherRef.current?.focus(); }
  }, [phase]);
  useEffect(() => {                                       // keep the panel inside the window when it is resized
    if (phase !== "open") return undefined;
    const onResize = () => setBox(measure());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [phase]);
  useEffect(() => {                                       // the full Ask BuildNexa page takes over: no half-finished animation left behind
    if (pathname.startsWith("/ask") && phaseRef.current !== "closed") setPhase("closed");
  }, [pathname]);
  useEffect(() => {                                       // a guided tour started from a reply needs the screen
    const hide = () => closePanel(false);
    window.addEventListener("buildnexa:start-guide", hide);
    return () => window.removeEventListener("buildnexa:start-guide", hide);
  }, [closePanel]);

  if (!user || loading || pathname.startsWith("/ask")) return null;
  if (!(user.role === "ORG_ADMIN" || canView("AI_ASSISTANT"))) return null;
  const first = (user.full_name || user.name || "there").split(" ")[0];
  const moving = phase === "opening" || phase === "closing";
  const shown = phase === "opening" || phase === "open";

  return (
    <div className="print:hidden">
      {mounted && box && (
        <PanelShell box={box} shown={shown} phase={phase} reduce={reduce} onDone={reduce ? finished : undefined}>
          <Panel key={session} name={first} active={phase === "open"}
            onMinimize={() => closePanel(false)} onClose={() => closePanel(true)}
            onReply={() => { if (phaseRef.current !== "open") setUnread((n) => n + 1); }} />
        </PanelShell>
      )}

      {moving && box && !reduce && <Flight box={box} up={phase === "opening"} onDone={finished} />}

      {phase === "closed" && (
        <div className={`bnx-bot fixed z-50 flex flex-col items-center${hello ? " bnx-talking" : ""}`} style={{ right: EDGE, bottom: EDGE - 10 }}>
          {hello && (
            <div className="bnx-say absolute bottom-[12px] right-[60px] w-60 rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-lg dark:border-slate-700 dark:bg-slate-800"
              role="status" data-testid="assistant-hello">
              <span className="absolute -right-[7px] bottom-4 h-3.5 w-3.5 rotate-45 border-r border-t border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800" aria-hidden="true" />
              <button type="button" onClick={dismissHello} className="absolute right-1.5 top-1.5 rounded p-0.5 text-slate-400 hover:text-slate-600" aria-label="Dismiss"><X className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={openPanel} className="relative block pr-4 text-left">
                <span className="block font-semibold text-slate-800 dark:text-slate-100">Hi {first} 👋</span>
                <span className="mt-0.5 block text-slate-600 dark:text-slate-300">Need help? Ask me for a report, or how to do any job in BuildNexa.</span>
              </button>
            </div>
          )}
          <button ref={launcherRef} type="button" onClick={openPanel} aria-label="Open BuildNexa AI Assistant" aria-haspopup="dialog" title="BuildNexa AI Assistant"
            className="bnx-bot-jump relative block rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-2"
            style={{ width: LAUNCHER, height: LAUNCHER }} data-testid="assistant-bubble">
            <img src={botIcon} alt="" className="bnx-glow h-full w-full rounded-full" draggable="false" />
            {unread > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white ring-2 ring-white dark:ring-slate-900" data-testid="assistant-unread">{unread}</span>}
          </button>
          <span className="bnx-bot-shadow mt-1 h-1.5 w-8 rounded-full bg-black/35 blur-[3px]" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}

/** The robot flying up (or down) the right edge, drawing a thin glowing line behind it that fades once it arrives. */
function Flight({ box, up, onDone }) {
  const end = 46;                                          // the robot settles at this size in the panel header's corner
  const startTop = box.vh - EDGE - LAUNCHER;
  const endTop = box.top + 12;
  const dy = startTop - endTop;
  const cx = EDGE + LAUNCHER / 2;                          // centre line, from the right edge
  const duration = up ? 0.6 : 0.55;
  return (
    <div className="pointer-events-none" aria-hidden="true">
      <motion.span
        className="fixed z-[51] block w-[2px] rounded-full"
        style={{
          right: cx - 1, bottom: EDGE + LAUNCHER / 2, height: dy, transformOrigin: up ? "50% 100%" : "50% 0%",
          background: `linear-gradient(to top, rgba(145,0,41,0) 0%, ${MAROON} 35%, #e11d48 100%)`,
          boxShadow: "0 0 6px 1px rgba(225,29,72,0.55)",
        }}
        initial={{ scaleY: up ? 0 : 1, opacity: up ? 0.95 : 0 }}
        animate={up ? { scaleY: [0, 1, 1], opacity: [0.95, 0.95, 0] } : { scaleY: [1, 1, 0], opacity: [0, 0.85, 0] }}
        transition={{ duration: duration + 0.25, times: up ? [0, 0.7, 1] : [0, 0.25, 1], ease: EASE }}
      />
      <motion.img
        src={botIcon} alt=""
        className="fixed z-[52] rounded-full shadow-[0_6px_18px_-2px_rgba(225,29,72,0.6)]"
        style={{ right: EDGE, top: startTop, width: LAUNCHER, height: LAUNCHER }}
        initial={up ? { y: 0, x: 0, scale: 1, opacity: 1 } : { y: -dy, x: 0, scale: end / LAUNCHER, opacity: 0 }}
        animate={up
          ? { y: [0, -dy * 0.55, -dy], x: [0, -10, 0], scale: [1, 1, end / LAUNCHER], opacity: [1, 1, 0] }
          : { y: [-dy, -dy * 0.45, 0], x: [0, -10, 0], scale: [end / LAUNCHER, 1, 1], opacity: [0, 1, 1] }}
        transition={{ duration, times: up ? [0, 0.6, 1] : [0, 0.45, 1], ease: EASE }}
        onAnimationComplete={onDone}
      />
    </div>
  );
}

/** The panel's frame: anchored bottom-right, it unfolds upward (clip from the top) and folds back down. */
function PanelShell({ box, shown, phase, reduce, onDone, children }) {
  const hidden = reduce ? { opacity: 0, y: 16 } : { clipPath: "inset(100% 0% 0% 0% round 16px)", opacity: 0.4, scale: 0.98 };
  const visible = reduce ? { opacity: 1, y: 0 } : { clipPath: "inset(0% 0% 0% 0% round 16px)", opacity: 1, scale: 1 };
  const transition = reduce ? { duration: 0.18 } : shown ? { duration: 0.55, delay: 0.08, ease: EASE } : { duration: 0.4, ease: [0.4, 0, 0.6, 1] };
  return (
    <motion.div
      role="dialog" aria-modal="false" aria-label="BuildNexa AI Assistant" aria-hidden={phase === "closed"}
      className={`fixed z-50 flex flex-col overflow-hidden rounded-2xl border border-rose-100 bg-white shadow-[0_24px_60px_-12px_rgba(110,0,31,0.35)] dark:border-rose-900/40 dark:bg-slate-900 ${phase === "closed" ? "invisible" : ""} ${phase === "open" ? "" : "pointer-events-none"}`}
      style={{ top: box.top, right: box.right, bottom: box.bottom, width: box.width, transformOrigin: "100% 100%" }}
      initial={hidden} animate={shown ? visible : hidden} transition={transition}
      onAnimationComplete={onDone}
      data-testid="assistant-panel" data-phase={phase}>
      {children}
    </motion.div>
  );
}

function Panel({ name, active, onMinimize, onClose, onReply }) {
  const { selectedCompanyId } = useCompany() || {};
  const [chatId, setChatId] = useState(recall);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {                         // reopen the chat from earlier in this login
    if (!chatId) return undefined;
    let alive = true;
    api.get(`/ai/chats/${chatId}`).then(({ data }) => alive && setMessages(data.messages)).catch(() => { if (alive) { setChatId(null); remember(null); } });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => { if (active) inputRef.current?.focus(); }, [active]);
  useEffect(() => { const el = scrollRef.current; if (el?.scrollTo) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); }, [messages, busy]);
  useEffect(() => {
    if (!active) return undefined;
    const esc = (e) => { if (e.key === "Escape") onMinimize(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [active, onMinimize]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    const before = messages;
    setMessages([...before, { role: "user", content }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { message: content, chat_id: chatId || null, company_id: selectedCompanyId || null });
      setMessages([...before, { role: "user", content }, { role: "assistant", content: data.reply }]);
      if (!chatId) { setChatId(data.chat_id); remember(data.chat_id); }
      onReply();
    } catch (err) {
      toast.error(getErrorMessage(err));
      setMessages(before);
      setInput(content);
    } finally { setBusy(false); }
  };
  const quick = (q) => {
    if (q.ask) { send(q.ask); return; }
    setInput(q.prefill || "");
    inputRef.current?.focus();
  };

  return (
    <>
      <div className="flex shrink-0 items-center gap-3 px-4 py-3 text-white" style={{ background: HEADER_BG }}>
        <img src={botIcon} alt="" className="h-10 w-10 shrink-0 rounded-full ring-2 ring-white/25" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold leading-tight">BuildNexa AI Assistant</div>
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-rose-100"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden="true" />ERP Support</div>
        </div>
        <button type="button" onClick={onMinimize} disabled={!active} className="rounded-md p-1.5 text-rose-50 hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60" title="Minimise" aria-label="Minimise" data-testid="assistant-minimise"><Minus className="h-4 w-4" /></button>
        <button type="button" onClick={onClose} disabled={!active} className="rounded-md p-1.5 text-rose-50 hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60" title="Close and start over" aria-label="Close" data-testid="assistant-close"><X className="h-4 w-4" /></button>
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-[#F7F8FA] px-4 py-4 text-sm leading-relaxed dark:bg-slate-900" aria-live="polite">
        <div className="flex items-start gap-2.5">
          <img src={botIcon} alt="" className="mt-0.5 h-8 w-8 shrink-0 rounded-full" />
          <div className="rounded-2xl rounded-tl-sm bg-white px-4 py-3 shadow-sm ring-1 ring-slate-100 dark:bg-slate-800 dark:ring-slate-700">
            <div className="font-semibold text-slate-800 dark:text-slate-100">Hello, {name} 👋</div>
            <div className="mt-1 text-slate-600 dark:text-slate-300">I can find reports from your live data and walk you through any job in BuildNexa.</div>
          </div>
        </div>

        {messages.length === 0 && (
          <div className="grid gap-2 pt-1">
            {QUICK.map((q) => (
              <button key={q.title} type="button" disabled={busy} onClick={() => quick(q)}
                className="group flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-left transition hover:border-rose-300 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-rose-500/50"
                data-testid="assistant-quick">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-50 text-[#910029] dark:bg-rose-500/15 dark:text-rose-300"><q.icon className="h-[18px] w-[18px]" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">{q.title}</span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400">{q.hint}</span>
                </span>
                <ChevronRight className="h-4 w-4 text-slate-400 transition group-hover:translate-x-0.5" />
              </button>
            ))}
          </div>
        )}

        {messages.map((m, i) => (m.role === "user"
          ? <div key={i} className="ml-auto max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-tr-sm px-4 py-2.5 text-white" style={{ background: MAROON }}>{m.content}</div>
          : <div key={i} className="max-w-full overflow-x-auto rounded-2xl rounded-tl-sm bg-white px-4 py-2.5 shadow-sm ring-1 ring-slate-100 dark:bg-slate-800 dark:ring-slate-700"><Markdown text={m.content} /></div>))}
        {busy && <Thinking />}
      </div>

      <div className="shrink-0 border-t border-slate-100 bg-white px-3 pb-2 pt-3 dark:border-slate-800 dark:bg-slate-900">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1.5 pl-4 pr-1.5 focus-within:border-rose-300 focus-within:ring-2 focus-within:ring-rose-100 dark:border-slate-700 dark:bg-slate-800 dark:focus-within:ring-rose-500/20">
          <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about your projects, reports or how to…"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400 dark:text-slate-100" aria-label="Message" data-testid="assistant-input" />
          <button type="submit" disabled={busy || !input.trim()} aria-label="Send"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white transition hover:brightness-110 disabled:opacity-40" style={{ background: MAROON }}>
            <SendHorizontal className="h-4 w-4" />
          </button>
        </form>
        <div className="mt-1.5 flex items-center justify-center gap-2 text-[11px] text-slate-400">
          <span>BuildNexa AI can make mistakes; check important figures.</span>
          <Link to={chatId ? `/ask?chat=${chatId}` : "/ask"} onClick={onMinimize} className="underline hover:text-slate-600">Full page</Link>
        </div>
      </div>
    </>
  );
}
